import type { EmailProviderCode, NotificationChannel, NotificationType } from './notification.types.js'

/** A tenant's sending-domain verification state (TO-382, mapped from the provider's DKIM status). */
export enum SenderDomainStatus {
	PENDING = 'PENDING',
	VERIFIED = 'VERIFIED',
	FAILED = 'FAILED',
}

/** The provider-neutral DKIM status an email domain-identity adapter reports (TO-382). */
export enum DomainDkimStatus {
	SUCCESS = 'SUCCESS',
	PENDING = 'PENDING',
	NOT_STARTED = 'NOT_STARTED',
	FAILED = 'FAILED',
	TEMPORARY_FAILURE = 'TEMPORARY_FAILURE',
}

export enum DnsRecordType {
	CNAME = 'CNAME',
	TXT = 'TXT',
}

export enum DnsRecordPurpose {
	DKIM = 'DKIM',
	SPF = 'SPF',
	DMARC = 'DMARC',
}

/** A condition the console shows next to the email settings (TO-382). */
export enum EmailSettingsWarning {
	/** The from address is on a domain that is not verified: email goes out from the TODAY address. */
	SENDER_DOMAIN_UNVERIFIED = 'SENDER_DOMAIN_UNVERIFIED',
}

/** One DNS record the tenant publishes to verify its sending domain. */
export interface SenderDomainDnsRecord {
	type: DnsRecordType
	purpose: DnsRecordPurpose
	name: string
	value: string
}

/** `POST …/email/domain-verification` response, and `senderDomain` of the settings read. */
export interface SenderDomainView {
	domain: string
	status: SenderDomainStatus
	dnsRecords: SenderDomainDnsRecord[]
	/** ISO-8601 UTC. */
	checkedAt: string
}

/** The stored email binding as the settings API shows it — never a secret or its reference. */
export interface EmailSettingsBinding {
	channel: NotificationChannel.EMAIL
	provider: EmailProviderCode
	fromAddress: string | null
	fromName: string | null
	replyTo: string | null
	/** The tenant's unsubscribe mailbox (TO-138): the `mailto:` of a marketing email's `List-Unsubscribe`. */
	unsubscribeMailbox: string | null
	hasCredentials: boolean
	updatedAt: string
}

export interface EmailSender {
	name: string
	address: string
}

/** `GET` / `PUT …/notification-settings/email` response (TO-382). */
export interface EmailSettingsResponse {
	binding: EmailSettingsBinding | null
	senderDomain: SenderDomainView | null
	effectiveSender: EmailSender
	warnings: EmailSettingsWarning[]
}

/** Bring-your-own SES credentials: write-only, never returned. */
export interface EmailSettingsCredentials {
	accessKeyId: string
	secretAccessKey: string
	region: string
}

/** `PUT …/notification-settings/email` body (TO-382). */
export interface EmailSettingsRequest {
	provider: EmailProviderCode
	fromAddress?: string | null
	fromName?: string | null
	replyTo?: string | null
	/** TO-138: absent = null (PUT replaces the binding). */
	unsubscribeMailbox?: string | null
	credentials?: EmailSettingsCredentials
}

/** One catalog entry of the tenant's notification toggles (TO-382). */
export interface NotificationToggle {
	notificationType: NotificationType
	channel: NotificationChannel
	enabled: boolean
	/** A required entry is always enabled and cannot be switched off. */
	required: boolean
}

/** One item of `PUT …/notification-settings/toggles`. */
export interface NotificationToggleInput {
	notificationType: NotificationType
	channel: NotificationChannel
	enabled: boolean
}

export interface NotificationToggleList {
	items: NotificationToggle[]
}
